import api from './Api';

// A function is prefered over a class in this situation to
// avoid having to declare the entire object in the constructor

export function CustomModule(baseRoute, logger, description) {
    return description;
}

export default function Module(baseRoute, logger, templateFunctions) {
    return {
        namespaced: true,

        state: {
            queries: {},
            data: {},
        },

        mutations: {
            setLoading(state, { queryRoute, value }) {
                let lastUpdated = !value ? Date.now() : undefined;
                state.queries = {
                    ...state.queries,
                    [queryRoute]: {
                        ...state.queries[queryRoute],
                        loading: value,
                        lastUpdated,
                    },
                };
            },
            setData(
                state,
                { queryRoute, data, template, pageInfo, queryInfo, res }
            ) {
                let temp = data.result || data;
                let formattedData = template[1](temp, templateFunctions, res);
                if (!formattedData.id) {
                    throw Error(
                        'Data does not contain an id field! Template lambda must return at minimum an id field',
                        formattedData
                    );
                }
                state.data = {
                    ...state.data,
                    [template[0] + formattedData.id]: formattedData,
                };
                state.queries = {
                    ...state.queries,
                    [queryRoute]: {
                        ...state.queries[queryRoute],
                        ids: formattedData.id,
                        pageInfo,
                        queryInfo,
                    },
                };
            },
            setDataWithoutQuery(state, { data, template, promiseResolver }) {
                let temp = data.result || data;
                let formattedData = template[1](temp, templateFunctions);
                if (formattedData.id) {
                    state.data = {
                        ...state.data,
                        [template[0] + formattedData.id]: formattedData,
                    };
                }
                return promiseResolver(formattedData);
            },
            setDataArray(
                state,
                { queryRoute, data, template, pageInfo, queryInfo, res }
            ) {
                logger.log('Set data array', data);
                let temp = data.result || data;
                let formattedData = temp
                    .map(it => it.result || it)
                    .map(it => template[1](it, templateFunctions, res));
                if (formattedData.length > 0 && !formattedData[0].id) {
                    throw Error(
                        'Data[0] does not contain an id field! Template lambda must return at minimun an id field',
                        formattedData
                    );
                }
                formattedData.forEach(
                    it =>
                        (state.data = {
                            ...state.data,
                            [template[0] + it.id]: it,
                        })
                );
                state.queries = {
                    ...state.queries,
                    [queryRoute]: {
                        ...state.queries[queryRoute],
                        ids: formattedData.map(it => it.id),
                        pageInfo,
                        queryInfo,
                    },
                };
            },
            setDataArrayWithoutQuery(
                state,
                { data, template, promiseResolver }
            ) {
                let temp = data.result || data;
                let formattedData = temp
                    .map(it => it.result || it)
                    .map(it => template[1](it, templateFunctions));
                if (formattedData[0].id) {
                    formattedData.forEach(
                        it =>
                            (state.data = {
                                ...state.data,
                                [template[0] + it.id]: it,
                            })
                    );
                }
                return promiseResolver(formattedData);
            },
            setError(state, { queryRoute, value }) {
                logger.log(
                    'committing setError on route',
                    queryRoute,
                    'with value',
                    value
                );
                state.queries = {
                    ...state.queries,
                    [queryRoute]: {
                        ...state.queries[queryRoute],
                        error: value,
                    },
                };
            },
            newQuery(state, { queryRoute, nextPage }) {
                logger.log('committed newQuery with route', queryRoute);
                state.queries = {
                    ...state.queries,
                    [queryRoute]: {
                        ids: [],
                        error: null,
                        loading: false,
                        lastUpdated: null,
                        pageInfo: null,
                        queryInfo: null,
                    },
                };
            },
            clearData(state) {
                //This function should only be called by clear!
                state.data = {};
            },
            clearQueries(state) {
                //This function should only be called by clear!
                state.queries = {};
            },
        },

        getters: {
            get: (
                state // From vuex
            ) => (
                store,
                baseRoute,
                template,
                authorize, // From interface
                baseURL,
                staticQuery,
                entry,
                methodObject
            ) => (
                {
                    forceUpdate,
                    authorize: shouldAuthorize,
                    baseURL: thisBaseURL,
                    reactive = false,
                    formData,

                    ...query
                },
                page = 1
            ) => {
                // From user
                let queryBase = { ...query, page };
                let storeNamespace = baseRoute.split('/')[0];
                let queryRoute =
                    baseRoute +
                    JSON.stringify(queryBase, Object.keys(queryBase).sort());
                let commitLoading = value =>
                    store.commit(storeNamespace + '/setLoading', {
                        queryRoute,
                        value,
                    });
                let queryFromStore = state.queries[queryRoute];

                const outputFunc = () => {
                    let queryFromStore = state.queries[queryRoute];
                    return {
                        ...queryFromStore,
                        data:
                            queryFromStore.ids instanceof Array
                                ? queryFromStore.ids.map(
                                      id => state.data[template[0] + id]
                                  )
                                : state.data[template[0] + queryFromStore.ids],
                        queryRoute,
                    };
                };

                logger.log('Getting query from route ' + queryRoute);
                if (
                    !queryFromStore ||
                    ((forceUpdate || !queryFromStore.lastUpdated) &&
                        !queryFromStore.loading)
                ) {
                    if (!queryFromStore) {
                        logger.log('Creating new query', queryRoute);
                        store.commit(storeNamespace + '/newQuery', {
                            queryRoute,
                        });
                    }
                    let commitData = (data, pageInfo, queryInfo, res) => {
                        if (/* !data || */ data === {} /*  || data === [] */)
                            return;
                        logger.log('Committing data', data);
                        store.commit(
                            (data.result || data || {}) instanceof Array
                                ? storeNamespace + '/setDataArray'
                                : storeNamespace + '/setData',
                            {
                                queryRoute,
                                template,
                                data,
                                pageInfo,
                                queryInfo,
                                res,
                            }
                        );
                    };

                    let commitError = value => {
                        logger.log('Error occured', value);
                        store.commit(storeNamespace + '/setError', {
                            queryRoute,
                            value:
                                value == undefined
                                    ? undefined
                                    : value.data || value,
                        });
                    };
                    let url = getUrl(baseRoute, {
                        ...staticQuery,
                        ...query,
                    });
                    commitLoading(true);
                    const apiQuery = api(
                        'GET',
                        url,
                        {},
                        null,
                        shouldAuthorize instanceof Boolean
                            ? shouldAuthorize
                            : authorize,
                        thisBaseURL instanceof String ? thisBaseURL : baseURL,
                        formData
                    )(templateFunctions)
                        .then(res => {
                            logger.log(
                                'Got data',
                                res,
                                'from baseRoute',
                                baseRoute,
                                'with entry',
                                entry
                            );
                            logger.log(res, 'template', template);
                            commitLoading(false);
                            commitError(undefined);
                            commitData(
                                entry(res),
                                template[2]
                                    ? template[2]['pageInfo'] &&
                                          template[2]['pageInfo'](res)
                                    : {},
                                methodObject['queryInfo']
                                    ? methodObject['queryInfo'](res, queryRoute)
                                    : {},
                                res
                            );
                            if (methodObject['action'])
                                methodObject['action'](res, queryRoute);
                        })
                        .catch(err => {
                            logger.log(
                                'Got error',
                                err,
                                'from baseRoute',
                                baseRoute
                            );
                            commitLoading(false);
                            commitError(err);
                            return Promise.reject(err);
                        });

                    if (!reactive) {
                        return apiQuery.then(outputFunc);
                    }
                }

                if (!reactive) {
                    return new Promise(async res => {
                        while (outputFunc().loading) {
                            await new Promise(res => {
                                setTimeout(() => {
                                    res();
                                }, 100);
                            });
                        }
                        return res(outputFunc());
                    });
                }

                return outputFunc();
            },
        },

        actions: {
            async clear({ commit }, baseRoute) {
                commit('clearData');
                commit('clearQueries');
            },
            async genericMethod({ commit, state }, payload) {
                // TODO: genericMethod doesn't use staticQuery yet
                if (
                    !payload.method ||
                    !payload.url ||
                    !(payload.body && payload.template)
                ) {
                    throw Error(
                        'action is missing essential payload items either method, url or template if body is supplied'
                    );
                }

                let commitData = (data, pageInfo) =>
                    new Promise(res => {
                        const d = payload.store.commit(
                            data.result instanceof Array
                                ? payload.baseRoute.split('/')[0] +
                                      '/setDataArrayWithoutQuery'
                                : payload.baseRoute.split('/')[0] +
                                      '/setDataWithoutQuery',
                            {
                                template: payload.template,
                                data,
                                promiseResolver: res,
                                pageInfo,
                            }
                        );
                    });
                logger.log(
                    'Executing query',
                    payload.method,
                    payload.url,
                    payload.query,
                    payload.body,
                    payload.authorize
                );
                return api(
                    payload.method,
                    payload.url,
                    payload.query,
                    payload.body,
                    payload.authorize,
                    payload.baseURL,
                    payload.formData
                )(templateFunctions).then(res => {
                    return commitData(
                        payload.entry(res),
                        payload.template[2]
                            ? payload.template[2]['pageInfo'] &&
                                  payload.template[2]['pageInfo'](res)
                            : {}
                    );
                });
            },
        },
    };
}

function getUrl(baseRoute, query) {
    return (baseRoute +=
        '?' +
        Object.entries(query)
            .filter(([k, v]) => (v instanceof Array ? v.length > 0 : true))
            .sort()
            .flatMap(([k, v]) =>
                v instanceof Array
                    ? v.sort().map(value => `${k}=${queryEncode(value)}`)
                    : `${k}=${queryEncode(v)}`
            )
            .join('&'));
}

function queryEncode(string) {
    return encodeURIComponent(string);
}
